import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Box, Typography, Stack, Button } from '@mui/material';
import { AppContext, AuthContext } from 'Context';
import { axios, baseUrl } from 'Api';
import { Pathname } from 'Routes';
import { isAffiliateUser } from 'Utils/settingsAccess';
import { beginUiSwitch } from 'Utils/uiSwitchOverlay';
import { Sidebar } from 'Components/sidebar';
import { Header } from 'Components/header';
import SoftAurora from 'Components/reactbits/SoftAurora';
import {
  AnimatedUser, AnimatedBell, AnimatedCreditCard, AnimatedLock, AnimatedCloud,
  AnimatedGroup, AnimatedFileText, AnimatedUserRoundPlus,
} from 'Components/reactbits/AnimatedIcons';
import LogoutButton from 'Components/LogoutButton';
import { V2ThemeProvider } from '../../_shared/V2Layout';
import { SpotlightCard } from '../../_shared/SpotlightCard';

const s = Pathname.settings;

// Bento spans pack exactly into a 4-col × 3-row grid (12 cells) so the whole
// board fits one viewport. `c`/`r` = column/row span on md+.
const ITEMS = [
  { key: 'account', title: 'Account', desc: 'Your profile, display name, and how friends see you.', Icon: AnimatedUser, path: s.accounts, c: 2, r: 2 },
  { key: 'notifications', title: 'Notifications', desc: 'Email and push preferences.', Icon: AnimatedBell, path: s.notifications, c: 1, r: 1 },
  // Billing was 1×2; it gave up a row so Referrals could join without pushing
  // the board onto a 4th (clipped) row.
  { key: 'billing', title: 'Billing & Payments', desc: 'Subscription, invoices, and payment methods.', Icon: AnimatedCreditCard, path: s.billings, c: 1, r: 1 },
  { key: 'manageUsers', title: 'User Management', desc: 'Users, roles, and access.', Icon: AnimatedGroup, path: s.manageUsers, c: 1, r: 1 },
  { key: 'cloud', title: 'Cloud', desc: 'Cloud storage and imports.', Icon: AnimatedCloud, path: s.cloud, c: 1, r: 1 },
  { key: 'referrals', title: 'Referrals', desc: 'Your invite link, signups, and gifts.', Icon: AnimatedUserRoundPlus, path: s.referrals, c: 1, r: 1 },
  { key: 'privacy', title: 'Privacy', desc: 'Privacy and security.', Icon: AnimatedLock, path: s.privacy, c: 1, r: 1 },
  { key: 'terms', title: 'Terms of Use', desc: 'Read the terms of use.', Icon: AnimatedFileText, path: s.termsAndConditions, c: 1, r: 1 },
  { key: 'subscriber', title: 'Subscriber Agreement', desc: 'Your subscriber agreement.', Icon: AnimatedFileText, path: s.subscriberAgreement, c: 1, r: 1 },
];

const SettingsHomeInner = () => {
  const navigate = useNavigate();
  const { setTopOrBottom, setAppSnackbar, userDetails } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  const [hovered, setHovered] = useState(null);
  const [referralKey, setReferralKey] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.post(`${baseUrl}/getMyReferralDetails`, { token: getToken() });
        if (mounted && data?.success) setReferralKey(data?.referral_details?.referral_key || '');
      } catch (e) { /* noop */ }
    })();
    return () => { mounted = false; };
  }, []);

  // "Refer & earn": copy the referral link (if the user has one) and open the
  // full Referrals page.
  const referAndEarn = async () => {
    const link = referralKey ? `https://www.projectorstream.com/referral/${referralKey}` : '';
    if (link) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(link);
        }
        if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'success', message: 'Referral link copied to clipboard' });
      } catch (e) { /* noop */ }
    }
    navigate(Pathname.getSettingsPath(Pathname.settings.referrals));
  };

  useEffect(() => {
    setTopOrBottom(true);
    return () => setTopOrBottom(false);
  }, []);

  const go = (path) => navigate(Pathname.getSettingsPath(path));

  // Affiliate accounts (allow-list in Utils/settingsAccess) get the Affiliate
  // Program tile in place of User Management — same cell, so the bento packing
  // is untouched.
  const affiliateUser = isAffiliateUser(userDetails?.email);
  const items = ITEMS.map((it) => (it.key === 'manageUsers' && affiliateUser
    ? {
      ...it,
      key: 'affiliate',
      title: 'Affiliate Program',
      desc: 'Your membership, connected accounts, and earnings.',
      path: Pathname.settings.affiliate,
      badge: 'New',
    }
    : it));

  return (
    <Box
      sx={{
        height: { xs: 'auto', md: '100vh' },
        minHeight: '100vh',
        overflow: { xs: 'visible', md: 'hidden' },
        // White page, dark glass cards.
        bgcolor: '#f4f5f7',
        color: '#15171c',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // /settings is a FloatingSidebar route (the rail), so reserve the same
        // 190px (16px offset + 168px rail + 6px gap) as content-layout-details,
        // matching the rail's own hide breakpoint (it hides below 1025px).
        pl: 0,
        pr: 0,
        '@media (min-width: 1025px)': { pl: '190px' },
        pt: '92px',
        pb: '96px',
        boxSizing: 'border-box',
      }}
    >
      <SoftAurora />
      <Box sx={{
        width: '86%',
        maxWidth: 'none',
        mx: 'auto',
        px: { xs: '12px', md: '20px' },
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1,
        // Above 1025px the rail already reserves its own space on the left
        // (outer Box's pl above), so centering at 86% here would stack an
        // extra proportional gutter on top of that and leave content
        // starting well right of the rail instead of flush with it, like
        // every other rail page. Fill the available width instead.
        '@media (min-width: 1025px)': { width: '100%', maxWidth: '100%', mx: 0 },
      }}
      >
        <Box sx={{
          mb: { xs: 2, md: 2.5 }, flexShrink: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2,
        }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 22, sm: 26, md: 30 }, color: '#15171c' }}>
              Settings
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: '#6b7280', mt: 0.5 }}>
              Manage your profile, preferences, privacy and billing in one place.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => beginUiSwitch(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: '#15171c',
                borderColor: '#d0d5dd',
                '&:hover': { borderColor: '#15171c', bgcolor: 'rgba(21,23,28,0.04)' },
              }}
            >
              Use classic layout
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={referAndEarn}
              startIcon={(
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="8" width="18" height="4" rx="1" />
                  <path d="M12 8v13" />
                  <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
                  <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8" />
                  <path d="M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
                </svg>
              )}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: '#15171c',
                borderColor: '#d0d5dd',
                '&:hover': { borderColor: '#15171c', bgcolor: 'rgba(21,23,28,0.04)' },
              }}
            >
              Refer &amp; earn
            </Button>
            <LogoutButton />
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gridTemplateRows: { md: 'repeat(3, 1fr)' },
            gridAutoRows: { xs: 'minmax(120px, auto)', md: 'auto' },
            gridAutoFlow: 'dense',
            gap: 1.75,
          }}
        >
          {items.map((it, i) => (
            <Box
              key={it.key}
              component={motion.div}
              initial={{ opacity: 0, y: 16, filter: 'blur(8px)', scale: 0.98 }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }}
              transition={{ duration: 0.45, delay: i * 0.05, ease: [0.4, 0, 0.2, 1] }}
              sx={{
                gridColumn: { xs: 'span 1', md: `span ${it.c}` },
                gridRow: { xs: 'span 1', md: `span ${it.r}` },
                minHeight: { xs: 120, md: 0 },
              }}
            >
              <SpotlightCard
                spotlightColor="rgba(90,165,239,0.10)"
                onClick={() => go(it.path)}
                onMouseEnter={() => setHovered(it.key)}
                onMouseLeave={() => setHovered(null)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') go(it.path); }}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  p: 2,
                  borderRadius: 0.75,
                  // White card — soft shadow instead of an outline (no wireframe look).
                  bgcolor: '#ffffff',
                  color: '#15171c',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 1px 3px rgba(15,17,28,0.06), 0 8px 22px rgba(15,17,28,0.05)',
                  transition: 'transform 0.2s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 16px 38px rgba(15,17,28,0.14)',
                  },
                }}
              >
                {/* Black icon on a light chip. */}
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: 1,
                    bgcolor: '#f1f3f5',
                    color: '#0a0a0a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <it.Icon size={30} strokeWidth={2.1} animate={hovered === it.key} />
                </Box>
                <Stack spacing={0.4} sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: '#15171c', lineHeight: 1.2 }}>{it.title}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.4 }}>{it.desc}</Typography>
                </Stack>
              </SpotlightCard>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const SettingsHomeV2 = () => (
  <>
    {/* Floating nav pill at the top. */}
    <Header slimV2 onMenuClick={() => {}} />
    <V2ThemeProvider>
      <SettingsHomeInner />
    </V2ThemeProvider>
    {/* Bottom dock suppressed — the floating rail is the primary nav here. */}
    <Sidebar noFixedSidebar sidebarIsVisible={false} onClose={() => {}} />
  </>
);

export default SettingsHomeV2;
